'use client'

import { cn } from '@gyym/ui'
import { useEffect, useRef, useState } from 'react'

interface FadeInProps {
  className?: string
  /** アニメーション時間 (ms) */
  duration?: number
  /** 開始までの遅延 (ms) */
  delay?: number
  /** 下から上へスライドする量 (px)。0 でフェードのみ */
  translateY?: number
  children: React.ReactNode
}

/**
 * Studio 版の「appear」アニメーションの再現。
 * ビューポートに入ったらフェードイン (+ 上方向スライド)。
 */
export const FadeIn = ({
  className,
  duration = 800,
  delay = 0,
  translateY = 30,
  children,
}: FadeInProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    if (reduceMotion || !('IntersectionObserver' in window)) {
      setShown(true)
      return
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          setShown(true)
          io.unobserve(el)
        })
      },
      { threshold: 0.1 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn(
        'transition-[opacity,transform] ease-[cubic-bezier(0.19,1,0.22,1)]',
        shown ? 'opacity-100' : 'opacity-0',
        className,
      )}
      style={{
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`,
        transform: shown ? 'none' : `translateY(${translateY}px)`,
      }}
    >
      {children}
    </div>
  )
}
